// DrawingListPanel.tsx
// Drawing set log table for the Properties tab.
// Columns: Sheet | Title | Discipline | Rev | Date | (delete row)
// Import modal: two tabs — "Upload PDF" and "Paste List"
// Calls /api/extract-drawing-list for both paths.

import React, { useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types & constants (self-contained — no dependency on drawingReviewData)
// ---------------------------------------------------------------------------
export interface DrawingListItem {
  id: string;
  sheet: string;
  title: string;
  discipline: string;
  revision: string;
  date: string;
}

export const DRAWING_DISCIPLINES = [
  "Architectural","Structural","Civil","Mechanical","Electrical",
  "Plumbing","Fire Protection","Landscape","Interiors","Other",
];

interface Props {
  drawingList: DrawingListItem[];
  onChange: (list: DrawingListItem[]) => void;
  /** Says what just happened; the third argument offers an Undo button. */
  showToast?: (msg: string, durationMs?: number, offerUndo?: boolean) => void;
  isLocked?: boolean;
  T: any;
}

type ImportTab = "pdf" | "paste";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function newRow(): DrawingListItem {
  return {
    id: `dl_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    sheet: "",
    title: "",
    discipline: "Architectural",
    revision: "",
    date: "",
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function DrawingListPanel({ drawingList, onChange, isLocked, T, showToast }: Props) {
  const [showImportModal, setShowImportModal] = useState(false);
  const [importTab, setImportTab] = useState<ImportTab>("pdf");
  const [pasteText, setPasteText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importFilename, setImportFilename] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The drawing list endpoint spends real money per call, so it authenticates.
  async function authHeaders(): Promise<Record<string, string>> {
    const { supabase } = await import("../lib/supabase");
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Please sign in to import a drawing list.");
    return { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` };
  }

  // ── Inline cell edit ──────────────────────────────────────────────────────
  function updateRow(id: string, field: keyof DrawingListItem, value: string) {
    onChange(drawingList.map(r => r.id === id ? { ...r, [field]: value } : r));
  }

  function deleteRow(id: string) {
    onChange(drawingList.filter(r => r.id !== id));
  }

  function addRow() {
    onChange([...drawingList, newRow()]);
  }

  function clearAll() {
    const n = drawingList.length;
    if (!n) return;
    onChange([]);
    showToast?.(`Cleared ${n} drawing${n === 1 ? "" : "s"}`, 6000, true);
  }

  // ── Import: PDF ───────────────────────────────────────────────────────────
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFilename(file.name);
    setImportError(null);

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setImportError("Please select a PDF file.");
      return;
    }

    setImporting(true);
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch("/api/extract-drawing-list", {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ source: "pdf", pdfBase64: base64, filename: file.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      applyImportedSheets(data.sheets || []);
      setShowImportModal(false);
    } catch (err: any) {
      setImportError(err.message || "Import failed.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // ── Import: Paste ─────────────────────────────────────────────────────────
  async function handlePasteImport() {
    if (!pasteText.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      const res = await fetch("/api/extract-drawing-list", {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ source: "text", text: pasteText, filename: "pasted-list" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      applyImportedSheets(data.sheets || []);
      setPasteText("");
      setShowImportModal(false);
    } catch (err: any) {
      setImportError(err.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  function applyImportedSheets(sheets: DrawingListItem[]) {
    // Merge — skip exact duplicates by sheet number
    const existing = new Set(drawingList.map(r => r.sheet.trim().toLowerCase()).filter(Boolean));
    const fresh = sheets.filter(s => !s.sheet || !existing.has(s.sheet.trim().toLowerCase()));
    onChange([...drawingList, ...fresh]);
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const headerStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    letterSpacing: "0.07em", textTransform: "uppercase",
    color: T.textMuted, marginBottom: 10, marginTop: 20,
    display: "flex", alignItems: "center", gap: 10,
  };
  const tableStyle: React.CSSProperties = {
    width: "100%", borderCollapse: "collapse", fontFamily: FONT, fontSize: 12,
  };
  const thStyle: React.CSSProperties = {
    textAlign: "left", padding: "5px 6px",
    fontFamily: FONT, fontSize: 10, fontWeight: 700,
    letterSpacing: "0.07em", textTransform: "uppercase",
    color: T.textMuted, borderBottom: `1px solid ${T.borderSecondary}`,
    background: "transparent",
  };
  const tdStyle: React.CSSProperties = {
    padding: "2px 4px", borderBottom: `1px solid ${T.borderSecondary}`,
    verticalAlign: "middle",
  };
  const cellInput: React.CSSProperties = {
    fontFamily: FONT, fontSize: 12, color: T.textSecondary,
    background: "transparent", border: "none", outline: "none",
    width: "100%", padding: "3px 2px",
  };
  const cellSelect: React.CSSProperties = {
    ...cellInput,
    cursor: "pointer",
  };
  const btnStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 11, fontWeight: 600,
    padding: "4px 10px", borderRadius: 6, cursor: "pointer",
    border: `1px solid ${T.borderSecondary}`,
    background: T.bgPanel ?? T.bgApp, color: T.textSecondary,
  };
  const importBtnStyle: React.CSSProperties = {
    ...btnStyle,
    background: T.borderAccent ?? "#5b9bd5",
    color: "#fff", border: "none", padding: "5px 12px",
  };
  const emptyStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 12, color: T.textWeak ?? T.textMuted,
    fontStyle: "italic", padding: "14px 8px", textAlign: "center",
  };

  // Modal overlay
  const overlayStyle: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
  };
  const modalStyle: React.CSSProperties = {
    background: T.bgPanel ?? T.bgApp, border: `1px solid ${T.borderSecondary}`,
    borderRadius: 10, padding: 24, width: 520, maxWidth: "95vw",
    boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
    fontFamily: FONT,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header row */}
      <div style={headerStyle}>
        <span>Drawing List</span>
        <span style={{ flex: 1 }} />
        {!isLocked && (
          <>
            <button style={importBtnStyle} onClick={() => { setShowImportModal(true); setImportError(null); }}>
              ⬆ Import
            </button>
            <button style={btnStyle} onClick={addRow}>+ Add Row</button>
            {drawingList.length > 0 && (
              <button style={{ ...btnStyle, color: T.textWeak ?? T.textMuted }} onClick={clearAll}>
                Clear All
              </button>
            )}
          </>
        )}
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 72 }}>Sheet</th>
              <th style={thStyle}>Title</th>
              <th style={{ ...thStyle, width: 130 }}>Discipline</th>
              <th style={{ ...thStyle, width: 64 }}>Rev</th>
              <th style={{ ...thStyle, width: 90 }}>Date</th>
              {!isLocked && <th style={{ ...thStyle, width: 28 }} />}
            </tr>
          </thead>
          <tbody>
            {drawingList.length === 0 ? (
              <tr>
                <td colSpan={isLocked ? 5 : 6} style={emptyStyle}>
                  No drawings yet — use Import to extract from a PDF or paste a drawing index.
                </td>
              </tr>
            ) : (
              drawingList.map(row => (
                <tr key={row.id}>
                  <td style={tdStyle}>
                    <input
                      style={cellInput}
                      value={row.sheet}
                      onChange={e => updateRow(row.id, "sheet", e.target.value)}
                      disabled={isLocked}
                      placeholder="A-101"
                      spellCheck={false}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      style={cellInput}
                      value={row.title}
                      onChange={e => updateRow(row.id, "title", e.target.value)}
                      disabled={isLocked}
                      placeholder="Sheet title"
                      spellCheck={false}
                    />
                  </td>
                  <td style={tdStyle}>
                    <select
                      style={cellSelect}
                      value={row.discipline}
                      onChange={e => updateRow(row.id, "discipline", e.target.value)}
                      disabled={isLocked}
                    >
                      {DRAWING_DISCIPLINES.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input
                      style={cellInput}
                      value={row.revision}
                      onChange={e => updateRow(row.id, "revision", e.target.value)}
                      disabled={isLocked}
                      placeholder="Rev 3"
                      spellCheck={false}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      style={cellInput}
                      value={row.date}
                      onChange={e => updateRow(row.id, "date", e.target.value)}
                      disabled={isLocked}
                      placeholder="02/14/2026"
                      spellCheck={false}
                    />
                  </td>
                  {!isLocked && (
                    <td style={{ ...tdStyle, textAlign: "center", paddingLeft: 0, paddingRight: 0 }}>
                      <button
                        onClick={() => deleteRow(row.id)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: T.textWeak ?? T.textMuted, fontSize: 14, padding: "2px 4px",
                          lineHeight: 1,
                        }}
                        title="Remove row"
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {drawingList.length > 0 && (
        <div style={{ fontFamily: FONT, fontSize: 11, color: T.textWeak ?? T.textMuted, marginTop: 6 }}>
          {drawingList.length} sheet{drawingList.length !== 1 ? "s" : ""} — will appear in the Scope of Work as Basis of Estimate.
        </div>
      )}

      {/* ── Import Modal ───────────────────────────────────────────────────── */}
      {showImportModal && (
        <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowImportModal(false); }}>
          <div style={modalStyle}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: T.textSecondary, flex: 1 }}>
                Import Drawing List
              </span>
              <button
                onClick={() => setShowImportModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20,
                  color: T.textMuted, lineHeight: 1, padding: "0 4px" }}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 2, marginBottom: 18, borderBottom: `1px solid ${T.borderSecondary}` }}>
              {(["pdf","paste"] as ImportTab[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => { setImportTab(tab); setImportError(null); }}
                  style={{
                    fontFamily: FONT, fontSize: 12, fontWeight: 600,
                    padding: "6px 14px", border: "none", cursor: "pointer",
                    borderBottom: importTab === tab ? `2px solid ${T.borderAccent ?? "#5b9bd5"}` : "2px solid transparent",
                    background: "none",
                    color: importTab === tab ? (T.borderAccent ?? "#5b9bd5") : T.textMuted,
                    marginBottom: -1,
                  }}
                >
                  {tab === "pdf" ? "📄 Upload PDF" : "📋 Paste List"}
                </button>
              ))}
            </div>

            {/* Tab: PDF */}
            {importTab === "pdf" && (
              <div>
                <p style={{ fontFamily: FONT, fontSize: 12, color: T.textMuted, marginTop: 0, marginBottom: 14, lineHeight: 1.6 }}>
                  Upload the cover sheet or drawing index PDF. Claude will extract every sheet number, title, discipline, revision, and date automatically.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  disabled={importing}
                  style={{ fontFamily: FONT, fontSize: 12, color: T.textSecondary, display: "block", marginBottom: 10 }}
                />
                {importFilename && !importing && (
                  <div style={{ fontFamily: FONT, fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
                    Selected: {importFilename}
                  </div>
                )}
                {importing && (
                  <div style={{ fontFamily: FONT, fontSize: 12, color: T.textMuted, fontStyle: "italic", marginBottom: 8 }}>
                    Extracting drawing list…
                  </div>
                )}
              </div>
            )}

            {/* Tab: Paste */}
            {importTab === "paste" && (
              <div>
                <p style={{ fontFamily: FONT, fontSize: 12, color: T.textMuted, marginTop: 0, marginBottom: 10, lineHeight: 1.6 }}>
                  Paste the drawing index or transmittal text below. Claude will parse sheet numbers, titles, disciplines, revisions, and dates.
                </p>
                <textarea
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  disabled={importing}
                  rows={10}
                  placeholder={"A-100  Site Plan  Architectural  Rev 3  02/14/2026\nA-101  First Floor Plan  Architectural  Rev 3  02/14/2026\nS-101  Foundation Plan  Structural  Rev 2  02/10/2026\n…"}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    fontFamily: "'Menlo','Consolas','monospace'", fontSize: 11,
                    background: T.bgApp, border: `1px solid ${T.borderSecondary}`,
                    borderRadius: 6, color: T.textSecondary,
                    padding: "8px 10px", resize: "vertical", outline: "none",
                  }}
                />
                <button
                  onClick={handlePasteImport}
                  disabled={importing || !pasteText.trim()}
                  style={{
                    ...importBtnStyle, marginTop: 10,
                    opacity: importing || !pasteText.trim() ? 0.5 : 1,
                  }}
                >
                  {importing ? "Extracting…" : "Extract Drawing List"}
                </button>
              </div>
            )}

            {/* Error */}
            {importError && (
              <div style={{ fontFamily: FONT, fontSize: 12, color: "#e05c5c", marginTop: 12 }}>
                {importError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DrawingListPanel;

// ---------------------------------------------------------------------------
// Util
// ---------------------------------------------------------------------------
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data-URL prefix ("data:application/pdf;base64,")
      const base64 = result.split(",")[1];
      if (base64) resolve(base64);
      else reject(new Error("Could not read file as base64."));
    };
    reader.onerror = () => reject(new Error("File read error."));
    reader.readAsDataURL(file);
  });
}
